import { useEffect, useRef, useState } from 'react';
import type { FundingResponse, FundingRow } from '../../shared/contract';
import { loadWithFallback } from '../lib/api';
import { basePath } from '../lib/paths';
import { fmtPct, fmtPrice, fmtRate } from '../lib/format';
import { log } from '../lib/log';
import { useInView } from '../site/motion';
import { prefersReducedMotion } from '../hooks/useReducedMotion';
import fundingSnapshot from '../data/funding.json';

const FUNDING_FALLBACK = fundingSnapshot as unknown as FundingResponse;

const ROUTES = [
  {
    from: 'Telegram',
    to: 'Discord',
    line: 'Anything the Telegram bot posts lands in your Discord channel the same second.',
  },
  {
    from: 'Discord',
    to: 'Telegram',
    line: 'Works the other way too, so a command run in Discord can answer in Telegram.',
  },
  {
    from: 'One bot',
    to: 'Both places',
    line: 'Same commands, same permissions, one codebase to keep up to date.',
  },
] as const;

export function Telegram() {
  const [ref, seen] = useInView<HTMLDivElement>(0.15);
  const [runKey, setRunKey] = useState(0);
  const [stage, setStage] = useState(0);
  const [pick, setPick] = useState<FundingRow | null>(null);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    let alive = true;
    loadWithFallback<FundingResponse>(basePath('/api/funding'), FUNDING_FALLBACK, controller.signal)
      .then((result) => {
        if (!alive) return;
        const extreme = [...result.data.rows].sort(
          (a, b) => Math.abs(b.annualisedRate) - Math.abs(a.annualisedRate),
        )[0];
        if (extreme) setPick(extreme);
        log.debug(
          'demo',
          `telegram demo picked ${extreme?.base} apr=${((extreme?.annualisedRate ?? 0) * 100).toFixed(2)}% source=${result.data.meta.source}`,
        );
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        log.warn('demo', 'telegram demo could not load a price', err);
      });
    return () => {
      alive = false;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    for (const t of timers.current) window.clearTimeout(t);
    timers.current = [];

    if (!seen) {
      setStage(0);
      return;
    }
    if (prefersReducedMotion()) {
      setStage(3);
      return;
    }

    setStage(0);
    [600, 1500, 2300].forEach((at, i) => {
      timers.current.push(window.setTimeout(() => setStage(i + 1), at));
    });

    return () => {
      for (const t of timers.current) window.clearTimeout(t);
      timers.current = [];
    };
  }, [seen, runKey]);

  const paying = (pick?.fundingRate ?? 0) >= 0 ? 'Longs are paying shorts' : 'Shorts are paying longs';
  const body = pick
    ? `${pick.base} funding is ${fmtRate(pick.fundingRate, 4)} every ${pick.fundingIntervalHours}h, about ${fmtPct(pick.annualisedRate * 100, 1)} a year. ${paying}. Mark ${fmtPrice(pick.markPrice)}.`
    : 'Funding update loading.';

  return (
    <div className="mk-case" ref={ref}>
      <div className="mk-case-head">
        <h2 className="mk-case-title">Telegram bots, linked to Discord</h2>
        <p className="mk-case-line">
          A Telegram bot on its own, or bridged so both communities see the same thing at once.
        </p>
      </div>

      <div className="mk-case-grid">
        <div className="mk-case-flow">
          <ul className="tgl-routes">
            {ROUTES.map((route) => (
              <li className="tgl-route" key={route.from}>
                <span className="tgl-route-head">
                  <span className="tgl-from">{route.from}</span>
                  <span className="tgl-arrow" aria-hidden="true">
                    &#8594;
                  </span>
                  <span className="tgl-to">{route.to}</span>
                </span>
                <span className="tgl-route-line">{route.line}</span>
              </li>
            ))}
          </ul>
          <button
            className="mk-replay"
            onClick={() => {
              setStage(0);
              setRunKey((k) => k + 1);
            }}
          >
            Run it again
          </button>
        </div>

        <div className="mk-case-out tgl-stack">
          <div className="tg tgl-panel" data-shown={stage >= 1 ? 'true' : 'false'}>
            <div className="tg-head">
              <img className="tg-avatar" src="/avatars/telegram.png" alt="" width={34} height={34} />
              <div>
                <p className="tg-name">steamed bot</p>
                <p className="tg-status">Telegram</p>
              </div>
            </div>
            <div className="tg-thread">
              <div className="tg-bubble tg-in">
                <p className="tg-line tg-strong">Market update</p>
                <p className="tg-line">{body}</p>
                <p className="tg-time">now</p>
              </div>
            </div>
          </div>

          <div className="tgl-bridge" data-active={stage >= 2 ? 'true' : 'false'}>
            <span className="tgl-bridge-line" aria-hidden="true" />
            <span className="tgl-bridge-label">bridged</span>
            <span className="tgl-bridge-line" aria-hidden="true" />
          </div>

          <div className="dc tgl-panel" data-shown={stage >= 3 ? 'true' : 'false'}>
            <div className="dc-head">
              <span className="dc-hash" aria-hidden="true">
                #
              </span>
              <span className="dc-channel">market-updates</span>
            </div>
            <div className="dc-body tgl-dcbody">
              <div className="dc-msg">
                <img className="dc-avatar" src="/avatars/yellow.png" alt="" width={40} height={40} />
                <div className="dc-msg-body">
                  <div className="dc-msg-head">
                    <span className="dc-author">steamed bot</span>
                    <span className="dc-tag">APP</span>
                    <span className="dc-time">Today at 21:44</span>
                  </div>
                  <div className="dc-embed" style={{ borderInlineStartColor: '#6cc79a' }}>
                    <p className="dc-embed-title">Market update</p>
                    <p className="dc-embed-sub">{body}</p>
                    <p className="dc-embed-foot">Forwarded from Telegram</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
